import React, { useMemo } from "react";
import { useSimulator } from "../Simulator/SimulatorStore";

type Recommendation = {
  title: string;
  description: string;
  severity: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
};

function SeverityBadge({ level }: { level: Recommendation["severity"] }) {
  const map = {
    CRITICAL: "bg-red-100 text-red-700",
    HIGH: "bg-amber-100 text-amber-700",
    MEDIUM: "bg-yellow-100 text-yellow-700",
    LOW: "bg-gray-100 text-gray-700",
  } as const;
  return (
    <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${map[level]}`}>{level}</span>
  );
}

export default function TopRecommendations() {
  const { processes, bottleneckIndex } = useSimulator();
  const items: Recommendation[] = useMemo(() => {
    const recs: Recommendation[] = [];

    if (bottleneckIndex >= 0) {
      const p = processes[bottleneckIndex];
      const util = Math.round(p.derived.utilizationPct);
      // Critical: add staff to bottleneck
      recs.push({
        title: `Add Staff to ${p.base.name}`,
        description: `Bottleneck ${util}% of time. Add 1-2 staff members.`,
        severity: "CRITICAL",
      });
    }

    // High: automate and improve maturity for top two processes shown in UI
    const contact = processes.find(pr => pr.base.name === "Contact Management");
    const pipeline = processes.find(pr => pr.base.name === "Pipeline Management");

    if (contact) {
      recs.push({
        title: `Automate ${contact.base.name}`,
        description: `No automation currently. Implement workflow automation to reduce processing time by up to 50%.`,
        severity: "HIGH",
      });
      recs.push({
        title: `Improve ${contact.base.name} Maturity`,
        description: `No formal processes. Implement SOPs and quality controls to reduce errors by 70%.`,
        severity: "HIGH",
      });
    }

    if (pipeline) {
      recs.push({
        title: `Automate ${pipeline.base.name}`,
        description: `No automation currently. Implement workflow automation to reduce processing time by up to 50%.`,
        severity: "HIGH",
      });
      recs.push({
        title: `Improve ${pipeline.base.name} Maturity`,
        description: `No formal processes. Implement SOPs and quality controls to reduce errors by 70%.`,
        severity: "HIGH",
      });
    }

    return recs.slice(0, 5);
  }, [processes, bottleneckIndex]);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Recommendations</h4>
      <div className="space-y-4">
        {items.map((rec, idx) => (
          <div key={idx} className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded bg-blue-500" />
                <h5 className="text-sm font-semibold text-gray-900">{rec.title}</h5>
              </div>
              <p className="ml-3 mt-1 text-sm text-gray-600">{rec.description}</p>
            </div>
            <SeverityBadge level={rec.severity} />
          </div>
        ))}
      </div>
    </div>
  );
}
